import React from 'react';
import { Box, Theme, Button, IconButton, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Close } from '@material-ui/icons';
import { ActivityDescription } from './ActivityDescription';
import { ActivitySession } from './ActivitySession';
import { ActivityRosteredInformation } from './ActivityTeamInformation';
import { ActivityAttachments } from './ActivityAttachments';
import { ActivityLocationInformation } from './ActivityLocationInformation';
import { ActivityUpcomingRosters } from './ActivityUpcomingRosters';
import { ActivityContextValue } from '../ActivityContext';
import { SessionActionType } from '../ActivityTypes';
import { useActivityActions } from '../useActivityActions';
import { sharedStyles } from '../ActivityStyles/shared';
import { StatusChip } from '../../../../common/chips/StatusChip';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { getRosterStatus, isActivityEnrolled, getSessionActions, isRosterEnrolled } from '../helpers';
import { ActivityRosterNotes } from './ActivityRosterNotes';
import { ActivityAttendance } from './ActivityAttendance';
import { useUser } from '../../../../global/auth/useUser';
import { RosterComments } from '../../../../common/comment/RosterComments';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      [theme.breakpoints.down('lg')]: {
        marginLeft: -16,
        marginRight: -16,
      },
      padding: '10px',
    },
    closeButton: {
      border: '1px solid',
      borderColor: theme.color.grey.neutralBrand200,
      borderRadius: '6px',
      backgroundColor: 'white',
      padding: '6px',
    },
  })
);
interface Props {
  inMyShift?: boolean;
}

export function ActivitySidebar({ inMyShift }: Props) {
  const classes = useStyles();
  const buttonClasses = sharedStyles.button();
  const { theme } = useCampfireTheme();
  const {
    activityDetails,
    vmVolunteer,
    query,
    vmVolunteerRefetch,
    enrolmentId,
    activityApplicationId,
    activityWaitlistingId,
    availabilityId,
    publishedRosteringId,
    availableSessionIds,
    rosteringSessionIds,
    sessionRosteringsWithTimes,
    setQuery,
    refetch,
  } = React.useContext(ActivityContextValue);
  const { onAction, onSessionAction } = useActivityActions(vmVolunteerRefetch);
  const { getVolunteerIdentity, user } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const { userIdentityService, configItems } = user;
  const activity = activityDetails?.vm.activity;

  const attendedSessions =
    activityDetails?.vm.activity?.sessions.filter((item) =>
      item.sessionReports
        .flatMap((s) => s.CICOs)
        .some(
          (c) =>
            c.volunteer.volunteerId === volunteerId &&
            c.checkIn &&
            c.checkOut &&
            c.sessionReport.activityReport.activity.activityId === activity?.activityId &&
            c.sessionReport.activityReport.activityDate === query.activityDate
        )
    ) || [];

  const getRosterStatusByDate = React.useCallback(
    (activityDate: string) => {
      if (activityDetails?.vm.activity && vmVolunteer) {
        return getRosterStatus(
          activityDetails?.vm.activity,
          activityDate,
          vmVolunteer,
          undefined,
          user.orgInfo.availabilityRestrictionDays
        );
      }
      return 'unsure';
    },
    [vmVolunteer, activityDetails, user.orgInfo.availabilityRestrictionDays]
  );

  const isEnrolled = React.useMemo(() => {
    const activityId = activityDetails?.vm.activity?.activityId;
    if (!activityId || !query.activityDate) {
      return false;
    }
    return isRosterEnrolled(activityId, query.activityDate, vmVolunteer?.vm.volunteer?.activityEnrolments);
  }, [vmVolunteer, activityDetails, query]);

  const actionButton = React.useMemo(() => {
    if (inMyShift) {
      return (
        <IconButton
          onClick={() => setQuery({ activityDate: undefined, activityId: undefined })}
          className={classes.closeButton}
        >
          <Close />
        </IconButton>
      );
    }
    if (!vmVolunteer || !activity || !query.activityDate) {
      return null;
    }
    const status = getRosterStatus(
      activity,
      query.activityDate,
      vmVolunteer,
      undefined,
      user.orgInfo.availabilityRestrictionDays
    );

    const roster = {
      ...activity,
      status,
      activityDate: query.activityDate,
    };

    if (['waitlisted', 'applied', 'rostered'].includes(status) && !activity.isRestrictedActivity) {
      return (
        <Button
          onClick={() =>
            onAction({ enrolmentId, activityApplicationId, activityWaitlistingId, publishedRosteringId })(
              'withdraw',
              roster
            )
          }
          classes={buttonClasses}
          variant='contained'
          color='primary'
        >
          Withdraw
        </Button>
      );
    }

    if (['not on team'].includes(status)) {
      return <StatusChip status='Joined' bgcolor={theme.palette.success.main} />;
    }

    return null;
  }, [vmVolunteer, activity, query, inMyShift]);

  const getActionBySession = React.useCallback(
    (sessionId) => {
      const isVolunteer = userIdentityService.privLevel === 'volunteer';
      const preventAvailability = configItems.vm.preventVolunteerAvailabilitySubmission === true;
      const allowUnavailability = configItems.vm.allowUnavailabilityAndLeave === true;

      if (isVolunteer && preventAvailability) {
        if (allowUnavailability && activity && query.activityDate && vmVolunteer) {
          const action = getSessionActions(sessionId, activity, query.activityDate, vmVolunteer);
          return action === 'unavailable' ? action : undefined;
        }
        return undefined;
      }
      if (activity && query.activityDate && vmVolunteer) {
        return getSessionActions(sessionId, activity, query.activityDate, vmVolunteer);
      }
      return undefined;
    },
    [
      activity,
      vmVolunteer,
      query,
      userIdentityService.privLevel,
      configItems.vm.preventVolunteerAvailabilitySubmission,
      configItems.vm.allowUnavailabilityAndLeave,
    ]
  );

  const onActionSession = (action: SessionActionType, sessionId: string) => {
    if (activity && query.activityDate) {
      onSessionAction({ enrolmentId, availabilityId, publishedRosteringId, availableSessionIds, rosteringSessionIds })(
        action,
        {
          ...activity,
          activityDate: query.activityDate,
          status: getRosterStatus(
            activity,
            query.activityDate,
            vmVolunteer,
            undefined,
            user.orgInfo.availabilityRestrictionDays
          ),
        },
        sessionId
      );
    }
  };

  // Filter upcoming rosters if see-rostered-dates-only is enabled for volunteers
  // Must be before early return to satisfy React hooks rules
  const filteredOccurrencesBetween = React.useMemo(() => {
    if (!activity?.occurrencesBetween) {
      return [];
    }
    if (userIdentityService.privLevel === 'volunteer' && configItems.vm.seeRosteredDatesOnly === true) {
      return activity.occurrencesBetween.filter((date) => {
        const status = getRosterStatusByDate(date);
        return status === 'rostered' || status === 'attended';
      });
    }
    return activity.occurrencesBetween;
  }, [
    activity?.occurrencesBetween,
    userIdentityService.privLevel,
    configItems.vm.seeRosteredDatesOnly,
    getRosterStatusByDate,
  ]);

  const rosterCommentsEnabled = React.useMemo(() => {
    const orgSetting = configItems.vm.allowRosterComments ?? false;
    const activitySetting = activity?.allowRosterComments;
    return activitySetting ?? orgSetting;
  }, [activity?.allowRosterComments, configItems.vm.allowRosterComments]);

  if (!activityDetails || !activity) {
    return null;
  }

  return (
    <Box position='relative' className={classes.container}>
      <ActivityDescription
        {...activity}
        inMyShift={inMyShift}
        activityId={activity.activityId}
        actionButton={actionButton}
      >
        <ActivityRosterNotes
          rosterNotes={activity.publishedRoster?.rosterNotes || activity.draftRoster?.rosterNotes}
          sessionNotes={activity.publishedRoster?.sessionNotes || activity.draftRoster?.sessionNotes || []}
        />
      </ActivityDescription>
      {(activity as any).isEvent &&
        !isEnrolled &&
        (() => {
          const rosterTemplates = (activity as any).rosterTemplates ?? [];
          const dateCount = rosterTemplates.length || 1;
          const sessionCount = activity.sessions?.length ?? 1;
          const isSingle = dateCount <= 1 && sessionCount <= 1;
          return (
            <Box mx={2} mt={1.5} mb={0.5} px={2} py={1.5} bgcolor={theme.color.grey.background} borderRadius={6}>
              <Typography style={{ fontSize: 12, color: theme.color.grey.neutral300, lineHeight: 1.6 }}>
                {isSingle
                  ? activity.hasOpenRoster
                    ? 'Join to be automatically rostered.'
                    : 'Join to be marked as available. The manager will finalise the roster.'
                  : activity.hasOpenRoster
                  ? `This event runs across ${dateCount} dates. After joining, select which dates and sessions you want to attend.`
                  : `This event runs across ${dateCount} dates. After joining, select your preferred dates and sessions.`}
              </Typography>
            </Box>
          );
        })()}
      {inMyShift ||
      (userIdentityService.privLevel === 'volunteer' && configItems.vm.seeRosteredDatesOnly === true) ? null : (
        <ActivityUpcomingRosters
          occurrencesBetween={filteredOccurrencesBetween}
          getRosterStatusByDate={getRosterStatusByDate}
        />
      )}
      {attendedSessions.length > 0 ? <ActivityAttendance sessions={attendedSessions} refetch={refetch} /> : null}
      <ActivitySession
        {...activity}
        getActionBySession={getActionBySession}
        onActionSession={onActionSession}
        showAction={false}
        isEnrolled={isEnrolled}
        sessionRosterings={rosteringSessionIds}
        sessionRosteringsWithTimes={sessionRosteringsWithTimes}
        activityDate={query.activityDate}
      />
      {inMyShift && (
        <ActivityRosteredInformation
          {...activity}
          isErolled={Boolean(isActivityEnrolled(activity.activityId, vmVolunteer?.vm.volunteer?.activityEnrolments))}
        />
      )}
      <ActivityLocationInformation {...activity} key={activity.activityId} />
      <ActivityAttachments {...activity} />
      {rosterCommentsEnabled && query.activityDate && (
        <RosterComments
          activityId={activity.activityId}
          activityDate={query.activityDate}
          rosterTemplateId={activity.rosterTemplate?.rosterTemplateId}
          compactInput
        />
      )}
    </Box>
  );
}
